// Slice B2.4-C of docs/planning/activity_dock_workflow_facilitation.md:
// pure session-scoped aggregation over the `ACTIVITY_DOCK_PEEK_EVENT`
// stream emitted by B2.4-B. The dev-only stats panel that consumes
// these helpers turns the structured peek-fire event into a live
// "which sections are pulling user attention?" view, answering the
// follow-up question B2.4-B left open: *which sections of the dock
// are deadweight that should lose their `hasSignal`?*
//
// Discipline notes:
//
//   1. **No persistence.** State lives in the React hook above; on
//      page reload it resets. This is intentional — the panel is for
//      *live* observation during a session, not historical analysis.
//      Cross-session attribution is B2.4-D (`activity_dock_peek_telemetry`
//      table + `/api/activity-dock/peek-stats` for platform_dev).
//   2. **Bounded memory.** Event log is capped at
//      `PEEK_STATS_MAX_EVENTS` (the oldest event is dropped on
//      overflow). 200 peeks is well past any realistic single
//      session — at the B2.1 fire rate (~one per mode-transition,
//      deduped per-session-per-mode) you'd need to flip the assist
//      level on/off dozens of times to hit it — and prevents a
//      runaway-events bug from leaking memory in a long-lived tab.
//   3. **Pure, deterministic.** `appendPeek` and `summarizePeekStats`
//      take state in, return state/summary out. Tests can exercise
//      the aggregation cleanly with no React / no DOM / no clock.
//   4. **Stable iteration order.** `topReasons` is sorted by count
//      descending, then by reasonSectionId ascending as the tie
//      break, so the panel UI renders deterministically across
//      re-summarizes with identical state.

import type { ActivityDockAssistLevel } from "./assistLevelTypes.js";
import type { ActivityDockPeekEventDetail } from "./peekEventDetail.js";

/** Maximum number of peek events retained in session memory. */
export const PEEK_STATS_MAX_EVENTS = 200;

/** Append-only event log keyed only by insertion order. */
export type PeekStatsState = {
  events: readonly ActivityDockPeekEventDetail[];
};

export function emptyPeekStatsState(): PeekStatsState {
  return { events: [] };
}

/**
 * Append a single peek-fire event to the log. Drops the oldest event
 * when the log would exceed `PEEK_STATS_MAX_EVENTS`. Returns a new
 * state object so React reference equality flips and consumers
 * re-render.
 */
export function appendPeek(
  state: PeekStatsState,
  event: ActivityDockPeekEventDetail,
): PeekStatsState {
  const next = state.events.length >= PEEK_STATS_MAX_EVENTS
    ? state.events.slice(state.events.length - PEEK_STATS_MAX_EVENTS + 1)
    : state.events.slice();
  next.push(event);
  return { events: next };
}

/** Pre-aggregated counts surfaced to the dev panel UI. */
export type PeekStatsSummary = {
  total: number;
  /** Map<reasonSectionId, count> */
  perReason: ReadonlyMap<string, number>;
  /** Map<mode id, count> */
  perMode: ReadonlyMap<string, number>;
  /** Map<ActivityDockAssistLevel, count> */
  perAssistLevel: ReadonlyMap<ActivityDockAssistLevel, number>;
  /**
   * Nested breakdown: Map<reasonSectionId, Map<PrimaryMode, count>>.
   * The panel uses this to render "ads-activity → leads (12), salesConsole (3)"
   * style rows so a reviewer can see which (section, mode) pairs are
   * the actual hot spots.
   */
  perReasonMode: ReadonlyMap<string, ReadonlyMap<string, number>>;
  /** Sorted descending by count; reasonSectionId ascending as tie-break. */
  topReasons: ReadonlyArray<{ reasonSectionId: string; count: number }>;
  /** Earliest event timestamp in the log, or `null` when empty. */
  firstTs: number | null;
  /** Latest event timestamp in the log, or `null` when empty. */
  lastTs: number | null;
};

/**
 * Walk the event log once, build all the aggregated views the panel
 * needs. O(N) over events; called from a `useMemo` keyed on state so
 * intermediate renders stay cheap.
 */
export function summarizePeekStats(state: PeekStatsState): PeekStatsSummary {
  const perReason = new Map<string, number>();
  const perMode = new Map<string, number>();
  const perAssistLevel = new Map<ActivityDockAssistLevel, number>();
  const perReasonMode = new Map<string, Map<string, number>>();
  let firstTs: number | null = null;
  let lastTs: number | null = null;

  for (const event of state.events) {
    perReason.set(event.reasonSectionId, (perReason.get(event.reasonSectionId) ?? 0) + 1);
    perMode.set(event.mode, (perMode.get(event.mode) ?? 0) + 1);
    perAssistLevel.set(event.assistLevel, (perAssistLevel.get(event.assistLevel) ?? 0) + 1);

    let modeBucket = perReasonMode.get(event.reasonSectionId);
    if (!modeBucket) {
      modeBucket = new Map<string, number>();
      perReasonMode.set(event.reasonSectionId, modeBucket);
    }
    modeBucket.set(event.mode, (modeBucket.get(event.mode) ?? 0) + 1);

    if (firstTs === null || event.ts < firstTs) firstTs = event.ts;
    if (lastTs === null || event.ts > lastTs) lastTs = event.ts;
  }

  const topReasons = Array.from(perReason.entries())
    .map(([reasonSectionId, count]) => ({ reasonSectionId, count }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.reasonSectionId.localeCompare(b.reasonSectionId);
    });

  return {
    total: state.events.length,
    perReason,
    perMode,
    perAssistLevel,
    perReasonMode,
    topReasons,
    firstTs,
    lastTs,
  };
}
